import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GROWTH_CATEGORIES, growthPosts } from '../data/growthPosts';
import { CALCULATORS } from '../data/calculators';
import { HUB_PATH, SERIES } from '../data/runYourClinic';
import './GrowthHub.css';

// Hub for the dental-growth content engine (strategy doc:
// aum-ehr-api/docs/growth/inbound-marketing-strategy.md). Every article
// funnels to the free Clinic Audit — the hub itself closes with the
// same CTA so a browser who reads nothing still sees the offer.
const GrowthHub = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    document.title = 'Dental Practice Growth Hub | Aumy by AUM AI';
  }, []);

  const posts =
    activeCategory === 'All'
      ? growthPosts
      : growthPosts.filter((p) => p.category === activeCategory);

  const sorted = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date));

  const formatDate = (dateStr) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  // Only show category chips that will ever match something, plus All —
  // empty categories will appear as articles get published.
  const visibleCategories = GROWTH_CATEGORIES.filter(
    (cat) => cat === 'All' || growthPosts.some((p) => p.category === cat)
  );

  return (
    <div className="ch-home gh-page">
      <section className="gh-hero">
        <div className="ch-container ch-narrow ch-center">
          <span className="ch-eyebrow">Dental Practice Growth</span>
          <h1 className="gh-hero-title">
            Practical answers to the question every clinic owner asks:
            <span className="gh-accent"> “why isn’t my clinic growing faster?”</span>
          </h1>
          <p className="ch-lead ch-center-lead">
            No jargon, no hype. Real numbers, real WhatsApp messages, and systems you can run
            this week — written from daily conversations with Indian dental clinic owners.
          </p>
        </div>
      </section>

      <section className="gh-list-section">
        <div className="ch-container">
          <div className="gh-filters">
            {visibleCategories.map((cat) => (
              <button
                key={cat}
                className={`gh-filter-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="gh-grid">
            {sorted.map((post) => (
              <Link key={post.slug} to={`/growth/${post.slug}`} className="gh-card">
                <span className="gh-card-category">{post.category}</span>
                <h2 className="gh-card-title">{post.title}</h2>
                <p className="gh-card-excerpt">{post.excerpt}</p>
                <div className="gh-card-meta">
                  <span>{formatDate(post.date)}</span>
                  <span className="gh-card-dot" />
                  <span>{post.readingTime}</span>
                </div>
              </Link>
            ))}
          </div>

          {sorted.length === 0 && (
            <p className="gh-empty">Articles in this category are on the way — check back soon.</p>
          )}

          {/* The teaching video series (owner 2026-10-13) — words in src/data/runYourClinic.js. */}
          <div className="gh-podcast-banner">
            <div>
              <span className="gh-card-category">{SERIES.eyebrow}</span>
              <h2 className="gh-card-title" style={{ marginTop: '10px' }}>{SERIES.name}</h2>
              <p className="gh-card-excerpt">{SERIES.hubBlurb}</p>
            </div>
            <Link to={HUB_PATH} className="ch-btn ch-btn-primary">
              Watch the series
            </Link>
          </div>

          <div className="gh-podcast-banner">
            <div>
              <span className="gh-card-category">The Dental Growth Podcast</span>
              <h2 className="gh-card-title" style={{ marginTop: '10px' }}>
                Are you a dentist, orthodontist, dental marketer, educator or entrepreneur with a
                story worth telling?
              </h2>
              <p className="gh-card-excerpt">
                We interview the people behind India's growing dental practices — the business side
                nobody teaches in college.
              </p>
            </div>
            <Link to="/podcast" className="ch-btn ch-btn-primary">
              Come on the podcast
            </Link>
          </div>

          <div className="gh-tools">
            <h2>Tools &amp; downloads</h2>
            <p className="gh-tools-sub">
              Put your own numbers in — the articles explain what slips through the cracks, these show you yours.
            </p>
            <div className="gh-grid">
              <Link to="/leak-calculator" className="gh-card">
                <span className="gh-tool-tag">Calculator</span>
                <h2 className="gh-card-title">What Slips Through the Cracks — 60-second Check</h2>
                <p className="gh-card-excerpt">
                  The full 60-second check: missed enquiries, no-shows and patients who never come
                  back, added up for the year.
                </p>
              </Link>
              <Link to="/missed-call-calculator" className="gh-card">
                <span className="gh-tool-tag">Calculator</span>
                <h2 className="gh-card-title">Missed Call Calculator</h2>
                <p className="gh-card-excerpt">
                  Four sliders, straight from your phone's call log — what unanswered calls cost
                  your clinic every month.
                </p>
              </Link>
              {CALCULATORS.map((c) => (
                <Link key={c.slug} to={`/${c.slug}`} className="gh-card">
                  <span className="gh-tool-tag">Calculator</span>
                  <h2 className="gh-card-title">{c.name}</h2>
                  <p className="gh-card-excerpt">{c.cardBlurb}</p>
                </Link>
              ))}
              <a
                href="/downloads/dental-clinic-revenue-leak-checklist.pdf"
                className="gh-card"
                download
              >
                <span className="gh-tool-tag">Free PDF</span>
                <h2 className="gh-card-title">Front-Desk Checklist</h2>
                <p className="gh-card-excerpt">
                  A one-page checklist of the six places patients slip through — print it and hand it to your
                  practice manager this week.
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="gh-cta-band">
        <div className="ch-container ch-center">
          <h2 className="gh-cta-title">Want to know what this looks like in your clinic?</h2>
          <p className="gh-cta-sub">
            Get a free Clinic Audit — where work gets stuck in your clinic and bookings slip through the cracks, and what
            each gap is worth. Prepared for your clinic, on your WhatsApp within 24 hours.
          </p>
          <div className="ch-hero-cta ch-center-cta">
            <Link to="/growth-audit" className="ch-btn ch-btn-primary">
              Get my free Clinic Audit
            </Link>
            <a
              href="https://wa.me/918007189868?text=Hi"
              target="_blank"
              rel="noopener noreferrer"
              className="ch-btn ch-btn-ghost ch-ghost-light"
            >
              See it live — WhatsApp Aumy now
            </a>
            <Link to="/leak-calculator" className="ch-btn ch-btn-ghost ch-ghost-light">
              Try the 60-second check
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GrowthHub;
